import React, { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import Price from 'components/Common/Price/Price';
import Image from 'components/Common/Medias/Image';
import useLargeSizeMode from 'hooks/ProductPageV2/useLargeSizeMode';
import { getDataFromAttributes } from 'services/data';
import { getUri } from 'services/generic';
import { getProductData } from 'services/gtm/gtmPageView';
import { formatProduct } from 'services/gtm/gtmUtils';
import { ENV_TWICPICS_URL_MEDIA } from 'settings/env';
import { GtmProductType } from 'types/GtmV2/GtmPageView';
import { ProductDetail } from 'types/Controls/ProductSheet';
import { ProductDataListing } from 'types/Product';

import styles from './Product.module.scss';

type Props = {
  /** Products attributes return by Octipas API */
  attributes?: { [key: string]: { Label: string; MainName: string } };
  /** Formatted product data */
  product: ProductDetail;
  /** Handle GTM event */
  sendEvent?: (product: GtmProductType) => void;
};

/**
 * A product.
 */
function Product({
  attributes,
  product,
  sendEvent,
}: Props): JSX.Element | null {
  const { LSModeEnabled } = useLargeSizeMode();
  const productDataListing = useMemo(
    () => getDataFromAttributes(product.Attributes, attributes || {}),
    [product.Attributes, attributes]
  );

  const {
    imgfacepivot: defaultVisuelFace,
    imgdospivot: defaultVisuelDos,
    imgdetailpivot: defaultVisuelDetail,
    imgfacecurvy: visuelFaceCurvy,
    imgdoscurvy: visuelDosCurvy,
    imgdetailcurvy: visuelDetailCurvy,
    decote: discountPercentage,
    libelle: subtitle,
    prix: price,
    prixbarre: preDiscountPrice,
    prixunique: uniquePrice,
    stickeropeco: commercialOperationSticker,
    stickerprincipal: principalSticker,
    stickersecondaire1: secondary1Sticker,
    stickersecondaire2: secondary2Sticker,
    metamarque,
    metalot,
  } = productDataListing as ProductDataListing;

  const formattedStickerCommercialOpe = commercialOperationSticker?.split('¤');
  const formattedPrincipalSticker = principalSticker?.split('¤');
  const preferCurvy = LSModeEnabled && visuelFaceCurvy?.length;
  const defaultVisuel = preferCurvy
    ? visuelFaceCurvy || defaultVisuelFace
    : defaultVisuelFace;
  const defaultVisuelHover =
    defaultVisuelDos || defaultVisuelDetail || defaultVisuelFace;
  const visuelHover = preferCurvy
    ? visuelDosCurvy ||
      visuelDetailCurvy ||
      visuelFaceCurvy ||
      defaultVisuelHover
    : defaultVisuelHover;
  const [productImage, setProductImage] = useState(defaultVisuel);

  return (
    <div className={styles.product}>
      <Link
        className={styles.productUrl}
        to={getUri(product.Url)}
        onClick={() =>
          sendEvent?.(getProductData(formatProduct(productDataListing)))
        }
      >
        <div className={styles.topSticker}>
          {commercialOperationSticker && (
            <span
              className={classNames(styles.sticker, 'noselect')}
              style={{
                background:
                  formattedStickerCommercialOpe.length > 1
                    ? formattedStickerCommercialOpe[1]
                    : '',
              }}
            >
              {formattedStickerCommercialOpe[0]}
            </span>
          )}
        </div>
        <div className={styles.bottomSticker}>
          {principalSticker && (
            <span className={classNames(styles.sticker, 'noselect')}>
              {formattedPrincipalSticker[0]}
              {formattedPrincipalSticker.length > 1 && (
                <img
                  className={styles.icon}
                  src={`${process.env.REACT_APP_WYSIWYG_OCTIPAS_URL}/sticker/${formattedPrincipalSticker[1]}_lp.png`}
                  alt="sticker"
                />
              )}
            </span>
          )}
        </div>
        <div
          onMouseEnter={() => {
            setProductImage(visuelHover);
          }}
          onMouseLeave={() => setProductImage(defaultVisuel)}
        >
          <Image
            alt={subtitle}
            className={styles.imageProduct}
            src={`${ENV_TWICPICS_URL_MEDIA}${productImage}`}
          />
        </div>
      </Link>
      <span className={styles.additionalInformations}>
        {secondary1Sticker} {secondary1Sticker && secondary2Sticker && ' - '}
        {secondary2Sticker}
      </span>
      <h2 className={styles.subtitle}>
        {metamarque ? `${metamarque}® - ${subtitle}` : subtitle}
      </h2>
      <Price
        priceIfDiscount={preDiscountPrice}
        price={price}
        uniquePrice={uniquePrice}
        discountPercentage={discountPercentage}
        newPriceClassName={styles.newPrice}
        lot={metalot}
      />
    </div>
  );
}

export default memo(Product);
